import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { ReceiptDialog } from '../components/Receipt.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, type Tone } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action, ApiError } from '../lib/api.ts';
import { useLiveAction } from '../lib/data.ts';
import { elapsed, money, qty as fmtQty } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import type { Catalog, FloorPlan, Line, OrderView } from '../lib/types.ts';
import { useNow, useUi } from '../lib/ui-state.tsx';
import { PayDialog } from './Pay.tsx';
import { OrderCustomer } from '../components/Customers.tsx';
import { ComboDialog, type ComboPick } from '../components/Combo.tsx';
import { isMaster, VariantDialog } from '../components/VariantDialog.tsx';
import { ProductTile } from '../components/ProductTile.tsx';
import { priceNow } from '../lib/menu.ts';
import { linePrice } from '../lib/taxes.ts';
import { PromoDialog } from '../components/PromoDialog.tsx';

const LINE_STATUS: Record<Line['kitchen_status'], { tone: Tone; icon: IconName; label: MessageKey }> = {
  pending: { tone: 'warn', icon: 'dot', label: 'line.pending' },
  sent: { tone: 'info', icon: 'flame', label: 'line.sent' },
  ready: { tone: 'ok', icon: 'bell', label: 'line.ready' },
  served: { tone: 'neutral', icon: 'check', label: 'line.served' },
};

interface Draft {
  product: string;
  qty: number;
  modifiers: string[];
  note?: string;
  course: number;
  combo?: ComboPick[];
  /** What it costs, to show it on the bill while it is on its way. */
  price?: number;
}

/** A line on the bill, or the lines of one combo menu shown together. */
type BillEntry = { line: Line; group: Line[] | null };
function billEntries(list: Line[]): BillEntry[] {
  const out: BillEntry[] = [];
  const seen = new Set<string>();
  for (const l of list) {
    if (!l.combo) out.push({ line: l, group: null });
    else if (!seen.has(l.combo)) {
      seen.add(l.combo);
      out.push({ line: l, group: list.filter((x) => x.combo === l.combo).sort((a, b) => (a.combo_seq ?? 0) - (b.combo_seq ?? 0)) });
    }
  }
  return out;
}
const STATUS_ORDER: Line['kitchen_status'][] = ['pending', 'sent', 'ready', 'served'];

export function Order() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { fail, toast } = useUi();
  const { can } = useSession();
  const now = useNow();
  const { data: view, setData: setView, live, reload } = useLiveAction<OrderView>(
    'restaurant.order',
    { order: id },
    ['restaurant'],
    (_e, p) => p.order === id || p.ticket !== undefined,
  );
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [category, setCategory] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [course, setCourse] = useState(1);
  const [pane, setPane] = useState<'menu' | 'bill'>('menu');
  const [choosing, setChoosing] = useState<string | null>(null);
  const [comboing, setComboing] = useState<string | null>(null);
  const [varying, setVarying] = useState<string | null>(null);
  const [editing, setEditing] = useState<BillEntry | null>(null);
  const [dialog, setDialog] = useState<null | 'pay' | 'bill' | 'move' | 'merge' | 'split' | 'discount' | 'promo' | 'cancel' | 'room' | 'more'>(null);
  const [sending, setSending] = useState(false);
  const [queued, setQueued] = useState<Draft[]>([]);
  const queue = useRef<Draft[]>([]);
  const flushTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The menu of the bill's own location: its prices, and only what it sells.
  const orderLocation = view?.order.location;
  useEffect(() => {
    if (orderLocation === undefined) return;
    action<Catalog>('products.catalog', { location: orderLocation ?? null }).then(setCatalog, fail);
  }, [fail, orderLocation]);

  const groupsFor = useCallback(
    (productId: string) => (catalog?.product_modifier_groups ?? []).filter((l) => l.product === productId),
    [catalog],
  );

  // Taps on plain products are queued for a moment and sent together, so fast tapping
  // feels instant and costs one request (GOLDEN-RULE.md, rule 10).
  const flush = useCallback(async () => {
    const items = queue.current;
    queue.current = [];
    if (items.length === 0) return;
    try {
      setView(await action<OrderView>('restaurant.add_items', { order: id, items: items.map(({ price: _price, ...item }) => item) }));
    } catch (err) {
      fail(err);
      void reload();
    } finally {
      setQueued((q) => q.slice(items.length));
    }
  }, [id, fail, reload, setView]);

  const add = (d: Draft) => {
    queue.current.push(d);
    setQueued((q) => [...q, d]);
    if (flushTimer.current) clearTimeout(flushTimer.current);
    flushTimer.current = setTimeout(() => void flush(), 350);
  };
  useEffect(() => () => void flush(), [flush]);

  const tapProduct = (productId: string) => {
    const product = catalog?.products.find((p) => p.id === productId);
    if (isMaster(product)) setVarying(productId);
    else if (product?.kind === 'combo') setComboing(productId);
    else if (groupsFor(productId).length > 0) setChoosing(productId);
    else add({ product: productId, qty: 1, modifiers: [], course });
  };

  const products = useMemo(() => {
    const list = catalog?.products ?? [];
    const q = search.trim().toLowerCase();
    // A search finds variants too; otherwise they are chosen inside their product.
    if (q) return list.filter((p) => !isMaster(p) && p.name.toLowerCase().includes(q));
    const cat = category ?? catalog?.categories[0]?.id;
    return list.filter((p) => p.category === cat && !p.variant_of);
  }, [catalog, category, search]);

  if (!view) {
    return (
      <Shell live={live}>
        <div className="order-layout">
          <div className="menu-pane">
            <Skeleton h={44} />
            <div className="product-grid">
              {Array.from({ length: 12 }, (_, i) => (
                <Skeleton key={i} h={96} r="var(--radius-md)" />
              ))}
            </div>
          </div>
          <aside className="bill-pane">
            <Skeleton h={300} />
          </aside>
        </div>
      </Shell>
    );
  }

  const { order, lines } = view;
  const isOpen = order.state === 'open';
  const active = lines.filter((l) => l.state === 'active');
  const pending = active.filter((l) => l.kitchen_status === 'pending');
  const courses = [...new Set([...lines.map((l) => l.course), course])].sort((a, b) => a - b);
  const productName = (pid: string) => catalog?.products.find((p) => p.id === pid)?.name ?? '';
  const productPrice = (pid: string) => {
    const p = catalog?.products.find((x) => x.id === pid);
    return p && catalog ? priceNow(catalog, p, new Date(now)).price : 0;
  };
  const itemCount = active.reduce((n, l) => n + l.qty, 0) + queued.reduce((n, q) => n + q.qty, 0);

  const send = async () => {
    await flush();
    setSending(true);
    try {
      setView(await action<OrderView>('restaurant.send', { order: id }));
      toast(t('order.sent_toast'));
    } catch (err) {
      fail(err);
    } finally {
      setSending(false);
    }
  };

  const status = isOpen
    ? pending.length > 0
      ? { tone: 'warn' as Tone, icon: 'send' as IconName, label: tn('order.unsent', pending.length) }
      : { tone: 'info' as Tone, icon: 'users' as IconName, label: t('order.open') }
    : order.state === 'paid'
      ? { tone: 'ok' as Tone, icon: 'checkCircle' as IconName, label: t('order.paid', { n: order.receipt_no ?? '' }) }
      : { tone: 'neutral' as Tone, icon: 'x' as IconName, label: t('order.cancelled') };

  return (
    <Shell live={live}>
      <div className={`order-layout pane-${pane}`}>
        <header className="order-head">
          <IconButton icon="back" label={t('order.back')} onClick={() => navigate('/pos')} />
          <div className="order-title">
            <h1>{order.name}</h1>
            <span className="order-meta">
              <span>#{order.number}</span>
              <span>
                <Icon name="users" size={14} /> {order.guests || '–'}
              </span>
              <span>
                <Icon name="clock" size={14} /> {elapsed(order.opened_at, now)}
              </span>
            </span>
          </div>
          <StatusBadge {...status} />
          {isOpen ? <IconButton icon="dots" label={t('order.more')} onClick={() => setDialog('more')} /> : null}
        </header>

        <div className="pane-switch" role="tablist">
          <button role="tab" aria-selected={pane === 'menu'} className={pane === 'menu' ? 'active' : ''} onClick={() => setPane('menu')}>
            <Icon name="search" size={16} /> {t('order.menu')}
          </button>
          <button role="tab" aria-selected={pane === 'bill'} className={pane === 'bill' ? 'active' : ''} onClick={() => setPane('bill')}>
            <Icon name="receipt" size={16} /> {t('order.bill')} <span className="count">{fmtQty(itemCount)}</span>
          </button>
        </div>

        <section className="menu-pane" aria-label={t('order.menu')}>
          {isOpen ? (
            <>
              <div className="search">
                <Icon name="search" size={18} />
                <input
                  className="input"
                  placeholder={t('order.search')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label={t('order.search')}
                />
                {search ? <IconButton icon="x" label={t('common.clear')} onClick={() => setSearch('')} /> : null}
              </div>
              {!search ? (
                <div className="category-tabs" role="tablist">
                  {(catalog?.categories ?? []).map((c) => {
                    const on = (category ?? catalog?.categories[0]?.id) === c.id;
                    return (
                      <button
                        key={c.id}
                        role="tab"
                        aria-selected={on}
                        className={`category-tab tone-${c.tone}${on ? ' active' : ''}`}
                        onClick={() => setCategory(c.id)}
                      >
                        {c.name}
                      </button>
                    );
                  })}
                </div>
              ) : null}
              <div className="product-grid">
                {!catalog
                  ? Array.from({ length: 12 }, (_, i) => <Skeleton key={i} h={96} r="var(--radius-md)" />)
                  : products.map((p) => {
                      const tone = catalog.categories.find((c) => c.id === p.category)?.tone ?? 'grey';
                      const inQueue = queued.filter((q) => q.product === p.id).reduce((n, q) => n + q.qty, 0);
                      const onBill =
                        (p.kind === 'combo'
                          ? new Set(active.filter((l) => l.combo_product === p.id).map((l) => l.combo)).size
                          : active
                              .filter((l) => (l.product === p.id || catalog.products.find((v) => v.id === l.product)?.variant_of === p.id) && !l.combo)
                              .reduce((n, l) => n + l.qty, 0)) + inQueue;
                      return (
                        <ProductTile
                          key={p.id}
                          product={p}
                          tone={tone}
                          price={priceNow(catalog, p, new Date(now))}
                          hint={p.kind === 'combo' ? t('product.combo') : isMaster(p) ? t('product.variants') : groupsFor(p.id).length ? t('product.options') : null}
                          count={onBill}
                          onTap={() => tapProduct(p.id)}
                        />
                      );
                    })}
                {catalog && products.length === 0 ? <EmptyState icon="search" title={t('order.no_results')} /> : null}
              </div>
            </>
          ) : (
            <EmptyState
              icon={order.state === 'paid' ? 'checkCircle' : 'x'}
              title={order.state === 'paid' ? t('order.closed_paid') : t('order.closed_cancelled')}
              action={
                <Button variant="primary" icon="table" onClick={() => navigate('/pos')}>
                  {t('order.back')}
                </Button>
              }
            />
          )}
        </section>

        <aside className="bill-pane" aria-label={t('order.bill')}>
          {view.order.customer !== undefined ? (
            <OrderCustomer order={view.order.id} customer={view.order.customer ?? null} open={isOpen} onChanged={() => void reload()} />
          ) : null}
          {isOpen ? (
            <div className="course-bar">
              <span className="muted">{t('order.course_for_new')}</span>
              <div className="segmented subtle">
                {[1, 2, 3].map((c) => (
                  <button key={c} className={course === c ? 'active' : ''} aria-pressed={course === c} onClick={() => setCourse(c)}>
                    {t('order.course', { n: c })}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <div className="bill-lines">
            {active.length === 0 && queued.length === 0 && lines.length === 0 ? (
              <EmptyState icon="receipt" title={t('order.empty')} body={t('order.empty_body')} />
            ) : (
              courses.map((c) => {
                const inCourse = lines.filter((l) => l.course === c);
                const queuedHere = queued.filter((q) => q.course === c);
                if (inCourse.length === 0 && queuedHere.length === 0) return null;
                return (
                  <div key={c} className="course-group">
                    <div className="course-label">{t('order.course', { n: c })}</div>
                    {billEntries(inCourse).map((entry) => {
                      const l = entry.line;
                      const group = entry.group;
                      const isVoid = l.state === 'void';
                      const status = group
                        ? STATUS_ORDER.find((st) => group.some((x) => x.kitchen_status === st))!
                        : l.kitchen_status;
                      const s = LINE_STATUS[status];
                      const total = group ? group.reduce((n, x) => n + linePrice(x), 0) : linePrice(l);
                      return (
                        <button
                          key={l.id}
                          className={`bill-line${isVoid ? ' void' : ''}${group ? ' combo' : ''}`}
                          disabled={!isOpen || isVoid}
                          onClick={() => setEditing(entry)}
                        >
                          <span className="line-qty">{fmtQty(l.qty)}×</span>
                          <span className="line-main">
                            <span className="line-name">{group ? l.combo_name : l.name}</span>
                            {group ? (
                              <span className="line-parts">
                                {group.map((x) => (
                                  <span key={x.id} className="line-part">
                                    {x.name}
                                    {x.modifiers.length ? <span className="line-mods"> · {x.modifiers.map((m) => m.name).join(', ')}</span> : null}
                                  </span>
                                ))}
                              </span>
                            ) : l.modifiers.length ? (
                              <span className="line-mods">{l.modifiers.map((m) => m.name).join(', ')}</span>
                            ) : null}
                            {l.price_rule ? (
                              <span className="line-deal">
                                <Icon name="clock" size={12} /> {l.price_rule}
                              </span>
                            ) : null}
                            {l.note ? (
                              <span className="line-note">
                                <Icon name="note" size={12} /> {l.note}
                              </span>
                            ) : null}
                            {isVoid ? <span className="line-void">{t('line.removed', { reason: l.void_reason ?? '' })}</span> : null}
                          </span>
                          <span className="line-side">
                            <span className="line-price">{money(total)}</span>
                            {!isVoid ? <StatusBadge tone={s.tone} icon={s.icon} label={t(s.label)} /> : null}
                          </span>
                        </button>
                      );
                    })}
                    {queuedHere.map((q, i) => (
                      <div key={`q${i}`} className="bill-line queued" aria-busy="true">
                        <span className="line-qty">{fmtQty(q.qty)}×</span>
                        <span className="line-main">
                          <span className="line-name">{productName(q.product)}</span>
                        </span>
                        <span className="line-side">
                          <span className="line-price">{money(q.price ?? productPrice(q.product) * q.qty)}</span>
                          <span className="spinner sm" aria-hidden="true" />
                        </span>
                      </div>
                    ))}
                  </div>
                );
              })
            )}
          </div>

          <div className="bill-foot">
            <dl className="totals">
              {order.discount > 0 || order.tax_added ? (
                <>
                  <dt>{t('totals.subtotal')}</dt>
                  <dd>{money(order.subtotal)}</dd>
                </>
              ) : null}
              {order.discount > 0 ? (
                <>
                  <dt>{t('totals.discount')}</dt>
                  <dd className="text-ok">−{money(order.discount)}</dd>
                </>
              ) : null}
              {order.tax_added ? (
                <>
                  <dt>{t('totals.sales_tax')}</dt>
                  <dd>{money(order.tax)}</dd>
                </>
              ) : (
                <>
                  <dt className="muted">{t('totals.vat_included')}</dt>
                  <dd className="muted">{money(order.tax)}</dd>
                </>
              )}
              {order.tip > 0 ? (
                <>
                  <dt>{t('totals.tip')}</dt>
                  <dd>{money(order.tip)}</dd>
                </>
              ) : null}
              <dt className="grand">{t('totals.total')}</dt>
              <dd className="grand">{money(order.total)}</dd>
            </dl>
            {isOpen ? (
              <div className="bill-actions">
                <Button
                  variant={pending.length > 0 || queued.length > 0 ? 'primary' : 'secondary'}
                  size="xl"
                  icon="send"
                  onClick={send}
                  busy={sending}
                  disabled={pending.length === 0 && queued.length === 0}
                >
                  {pending.length + queued.length > 0
                    ? t('order.send_n', { n: fmtQty(pending.reduce((n, l) => n + l.qty, 0) + queued.reduce((n, q) => n + q.qty, 0)) })
                    : t('order.send')}
                </Button>
                {can('pos.pay') ? (
                  <Button
                    variant={pending.length === 0 && queued.length === 0 && active.length > 0 ? 'success' : 'secondary'}
                    size="xl"
                    icon="card"
                    onClick={() => setDialog('pay')}
                    disabled={active.length === 0 || pending.length > 0 || queued.length > 0}
                    title={pending.length > 0 ? t('order.send_first') : undefined}
                  >
                    {t('order.pay')}
                  </Button>
                ) : null}
              </div>
            ) : (
              <Button variant="primary" size="xl" icon="table" onClick={() => navigate('/pos')} className="w-full">
                {t('order.back')}
              </Button>
            )}
          </div>
        </aside>
      </div>

      {choosing && catalog ? (
        <OptionsDialog
          catalog={catalog}
          productId={choosing}
          course={course}
          onClose={() => setChoosing(null)}
          onAdd={(d) => {
            add(d);
            setChoosing(null);
          }}
        />
      ) : null}
      {varying && catalog ? (
        <VariantDialog
          catalog={catalog}
          master={varying}
          at={new Date(now)}
          onClose={() => setVarying(null)}
          onPick={(v) => (groupsFor(v).length > 0 ? setChoosing(v) : add({ product: v, qty: 1, modifiers: [], course }))}
        />
      ) : null}
      {comboing && catalog ? (
        <ComboDialog
          catalog={catalog}
          comboId={comboing}
          onClose={() => setComboing(null)}
          onAdd={(c) => {
            add({ product: comboing, qty: c.qty, modifiers: [], note: c.note, course, combo: c.picks, price: c.total });
            setComboing(null);
          }}
        />
      ) : null}
      {editing ? <LineDialog line={editing.line} group={editing.group} onClose={() => setEditing(null)} onChanged={setView} /> : null}
      {dialog === 'more' ? <MoreDialog onClose={() => setDialog(null)} onPick={setDialog} hasItems={view.lines.some((l) => l.state === 'active')} /> : null}
      {dialog === 'pay' ? (
        <PayDialog
          view={view}
          onClose={() => setDialog(null)}
          onPaid={(v) => {
            setView(v);
            setDialog(null);
            navigate('/pos');
          }}
        />
      ) : null}
      {dialog === 'bill' ? <ReceiptDialog order={view.order.id} printNow onClose={() => setDialog(null)} /> : null}
      {dialog === 'move' ? <MoveDialog view={view} onClose={() => setDialog(null)} onDone={setView} /> : null}
      {dialog === 'room' ? <RoomDialog view={view} onClose={() => setDialog(null)} onDone={() => navigate('/pos')} /> : null}
      {dialog === 'merge' ? <MergeDialog view={view} onClose={() => setDialog(null)} /> : null}
      {dialog === 'split' ? <SplitDialog view={view} onClose={() => setDialog(null)} onDone={setView} /> : null}
      {dialog === 'discount' ? <DiscountDialog view={view} onClose={() => setDialog(null)} onDone={setView} /> : null}
      {dialog === 'promo' ? <PromoDialog view={view} onClose={() => setDialog(null)} onDone={setView} /> : null}
      {dialog === 'cancel' ? <CancelDialog view={view} onClose={() => setDialog(null)} /> : null}
    </Shell>
  );
}

function OptionsDialog({
  catalog,
  productId,
  course,
  onClose,
  onAdd,
}: {
  catalog: Catalog;
  productId: string;
  course: number;
  onClose: () => void;
  onAdd: (d: Draft) => void;
}) {
  const product = catalog.products.find((p) => p.id === productId)!;
  const groups = catalog.product_modifier_groups
    .filter((l) => l.product === productId)
    .map((l) => catalog.modifier_groups.find((g) => g.id === l.group)!)
    .filter(Boolean);
  const [chosen, setChosen] = useState<string[]>(() =>
    groups.flatMap((g) => (g.min_select > 0 ? catalog.modifiers.filter((m) => m.group === g.id).slice(0, g.min_select).map((m) => m.id) : [])),
  );
  const [count, setCount] = useState(1);
  const [note, setNote] = useState('');
  const toggle = (groupId: string, modId: string, max: number) => {
    setChosen((c) => {
      if (c.includes(modId)) return c.filter((x) => x !== modId);
      const inGroup = c.filter((x) => catalog.modifiers.find((m) => m.id === x)?.group === groupId);
      if (max === 1) return [...c.filter((x) => !inGroup.includes(x)), modId];
      if (max > 0 && inGroup.length >= max) return c;
      return [...c, modId];
    });
  };
  const missing = groups.find((g) => chosen.filter((x) => catalog.modifiers.find((m) => m.id === x)?.group === g.id).length < g.min_select);
  const unit = priceNow(catalog, product, new Date()).price + chosen.reduce((s, id) => s + (catalog.modifiers.find((m) => m.id === id)?.price_delta ?? 0), 0);

  return (
    <Dialog
      title={product.name}
      onClose={onClose}
      footer={
        <>
          <div className="stepper" aria-label={t('common.quantity')}>
            <IconButton icon="minus" label={t('common.less')} onClick={() => setCount((n) => Math.max(1, n - 1))} />
            <span className="stepper-value">{count}</span>
            <IconButton icon="plus" label={t('common.more')} onClick={() => setCount((n) => n + 1)} />
          </div>
          <Button
            variant="primary"
            size="lg"
            icon="plus"
            disabled={!!missing}
            onClick={() => onAdd({ product: productId, qty: count, modifiers: chosen, note: note.trim() || undefined, course })}
          >
            {t('options.add', { price: money(unit * count) })}
          </Button>
        </>
      }
    >
      {groups.map((g) => (
        <fieldset key={g.id} className="option-group">
          <legend>
            {g.name}{' '}
            <span className="muted">
              {g.min_select > 0 ? t('options.required') : t('options.optional')}
              {g.max_select > 1 ? ` · ${t('options.up_to', { n: g.max_select })}` : ''}
            </span>
          </legend>
          <div className="option-list">
            {catalog.modifiers
              .filter((m) => m.group === g.id)
              .map((m) => {
                const on = chosen.includes(m.id);
                return (
                  <button
                    key={m.id}
                    role={g.max_select === 1 ? 'radio' : 'checkbox'}
                    aria-checked={on}
                    className={`option${on ? ' active' : ''}`}
                    onClick={() => toggle(g.id, m.id, g.max_select)}
                  >
                    <span className="option-check">{on ? <Icon name="check" size={16} /> : null}</span>
                    <span className="option-name">{m.name}</span>
                    {m.price_delta ? <span className="option-price">{money(m.price_delta, { sign: true })}</span> : null}
                  </button>
                );
              })}
          </div>
        </fieldset>
      ))}
      <Field label={t('options.note')} hint={t('options.note_hint')}>
        <input className="input" value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} />
      </Field>
      {missing ? (
        <p className="form-hint">
          <Icon name="alert" size={14} /> {t('options.choose', { group: missing.name })}
        </p>
      ) : null}
    </Dialog>
  );
}

function LineDialog({ line, group, onClose, onChanged }: { line: Line; group: Line[] | null; onClose: () => void; onChanged: (v: OrderView) => void }) {
  const { fail, toast } = useUi();
  const { can } = useSession();
  const [count, setCount] = useState(line.qty);
  const [note, setNote] = useState(line.note ?? '');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const sent = (group ?? [line]).some((l) => l.kitchen_status !== 'pending');
  const name = group ? (line.combo_name ?? line.name) : line.name;

  const save = async () => {
    setBusy(true);
    try {
      onChanged(await action<OrderView>('restaurant.update_line', { line: line.id, qty: count, note: note.trim() || null }));
      onClose();
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  };
  const remove = async () => {
    setBusy(true);
    try {
      onChanged(await action<OrderView>('restaurant.remove_line', { line: line.id, ...(sent ? { reason: reason.trim() } : {}) }));
      toast(sent ? t('line.removed_toast_kitchen') : t('line.removed_toast'));
      onClose();
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <Dialog
        title={t('line.remove_sent_title', { name })}
        onClose={onClose}
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button variant="danger" icon="trash" onClick={remove} busy={busy} disabled={!can('restaurant.void') || reason.trim().length < 3}>
              {t('line.remove')}
            </Button>
          </>
        }
      >
        <p>{t('line.remove_sent_body')}</p>
        {can('restaurant.void') ? (
          <Field label={t('line.reason')} hint={t('line.reason_hint')}>
            <input className="input" value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} data-autofocus />
          </Field>
        ) : (
          <p className="callout callout-warn">
            <Icon name="alert" size={16} /> {t('line.manager_needed')}
          </p>
        )}
      </Dialog>
    );
  }

  return (
    <Dialog
      title={name}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="danger" icon="trash" onClick={remove} busy={busy}>
            {t('line.remove')}
          </Button>
          <Button variant="primary" icon="check" onClick={save} busy={busy}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="stepper lg" aria-label={t('common.quantity')}>
        <IconButton icon="minus" label={t('common.less')} onClick={() => setCount((n) => Math.max(1, n - 1))} />
        <span className="stepper-value">{fmtQty(count)}</span>
        <IconButton icon="plus" label={t('common.more')} onClick={() => setCount((n) => n + 1)} />
      </div>
      {group ? <p className="muted">{t('combo.in_menu', { items: group.map((x) => x.name).join(', ') })}</p> : null}
      <Field label={t('options.note')} hint={t('options.note_hint')}>
        <input className="input" value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} />
      </Field>
    </Dialog>
  );
}

type MoreKey = 'bill' | 'room' | 'move' | 'merge' | 'split' | 'discount' | 'promo' | 'cancel';
function MoreDialog({ onClose, onPick, hasItems }: { onClose: () => void; onPick: (d: MoreKey) => void; hasItems: boolean }) {
  const { can } = useSession();
  const items: { key: MoreKey; icon: IconName; show: boolean; danger?: boolean }[] = [
    { key: 'bill', icon: 'receipt', show: hasItems },
    { key: 'room', icon: 'bed', show: hasItems && can('hotel.charge') },
    { key: 'move', icon: 'move', show: true },
    { key: 'merge', icon: 'merge', show: true },
    { key: 'split', icon: 'split', show: true },
    { key: 'discount', icon: 'percent', show: can('sales.discount') },
    { key: 'promo', icon: 'gift', show: hasItems && can('loyalty.promote') },
    { key: 'cancel', icon: 'trash', show: true, danger: true },
  ];
  return (
    <Dialog title={t('order.more')} onClose={onClose} size="sm">
      <div className="menu-list">
        {items
          .filter((i) => i.show)
          .map((i) => (
            <button key={i.key} className={`menu-item${i.danger ? ' danger' : ''}`} onClick={() => onPick(i.key)}>
              <Icon name={i.icon} />
              <span>
                <strong>{t(`more.${i.key}` as MessageKey)}</strong>
                <span className="muted">{t(`more.${i.key}_hint` as MessageKey)}</span>
              </span>
              <Icon name="chevronRight" size={18} />
            </button>
          ))}
      </div>
    </Dialog>
  );
}

/** Puts the bill on a hotel guest's room; the guest pays it with the room at check-out. */
function RoomDialog({ view, onClose, onDone }: { view: OrderView; onClose: () => void; onDone: () => void }) {
  const { fail, toast } = useUi();
  const [rooms, setRooms] = useState<{ id: string; number: number; room: string; guest: string; departure: string }[] | null>(null);
  const [pick, setPick] = useState<{ id: string; room: string; guest: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');
  useEffect(() => {
    action<{ id: string; number: number; room: string; guest: string; departure: string }[]>('hotel.in_house', { location: view.order.location ?? null }).then(setRooms, fail);
  }, [view.order.location, fail]);
  const list = (rooms ?? []).filter((r) => !q.trim() || r.room.toLowerCase().includes(q.trim().toLowerCase()) || r.guest.toLowerCase().includes(q.trim().toLowerCase()));
  if (pick) {
    return (
      <Confirm
        title={t('more.room_confirm_title', { room: pick.room })}
        body={t('more.room_confirm', { total: money(view.order.total), room: pick.room, guest: pick.guest })}
        confirm={t('more.room_submit')}
        busy={busy}
        onClose={() => setPick(null)}
        onConfirm={async () => {
          setBusy(true);
          try {
            await action('hotel.charge_to_room', { order: view.order.id, stay: pick.id });
            toast(t('more.room_done', { room: pick.room }));
            onDone();
          } catch (e) {
            fail(e);
            setBusy(false);
          }
        }}
      />
    );
  }
  return (
    <Dialog title={t('more.room')} onClose={onClose} size="sm">
      <div className="form-stack">
        <label className="search">
          <Icon name="search" size={16} />
          <input className="input" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('more.room_search')} aria-label={t('more.room_search')} data-autofocus />
        </label>
        {!rooms ? (
          <Skeleton h={160} />
        ) : !list.length ? (
          <p className="muted">{rooms.length ? t('more.room_none_found') : t('more.room_empty')}</p>
        ) : (
          <div className="menu-list">
            {list.map((r) => (
              <button key={r.id} className="menu-item" onClick={() => setPick(r)}>
                <Icon name="bed" />
                <span>
                  <strong>{t('more.room_n', { room: r.room })}</strong>
                  <span className="muted">{r.guest}</span>
                </span>
                <Icon name="chevronRight" size={18} />
              </button>
            ))}
          </div>
        )}
      </div>
    </Dialog>
  );
}

function useFloorPlan() {
  const [plan, setPlan] = useState<FloorPlan | null>(null);
  const { fail } = useUi();
  useEffect(() => {
    action<FloorPlan>('restaurant.floor_plan', {}).then(setPlan, fail);
  }, [fail]);
  return plan;
}

function MoveDialog({ view, onClose, onDone }: { view: OrderView; onClose: () => void; onDone: (v: OrderView) => void }) {
  const plan = useFloorPlan();
  const { fail, toast } = useUi();
  const move = async (table: string) => {
    try {
      const v = await action<OrderView>('restaurant.move_table', { order: view.order.id, table });
      onDone(v);
      toast(t('more.moved', { name: v.order.name }));
      onClose();
    } catch (err) {
      fail(err);
    }
  };
  const free = (plan?.tables ?? []).filter((x) => !x.order);
  return (
    <Dialog title={t('more.move')} onClose={onClose}>
      {!plan ? (
        <Skeleton h={120} />
      ) : free.length === 0 ? (
        <EmptyState icon="table" title={t('more.no_free_tables')} />
      ) : (
        plan.floors.map((f) => {
          const here = free.filter((x) => x.floor === f.id);
          if (!here.length) return null;
          return (
            <div key={f.id} className="pick-section">
              <h3>{f.name}</h3>
              <div className="pick-grid">
                {here.map((x) => (
                  <button key={x.id} className="pick" onClick={() => void move(x.id)}>
                    <strong>{x.name}</strong>
                    <span className="muted">{tn('table.seats', x.seats)}</span>
                  </button>
                ))}
              </div>
            </div>
          );
        })
      )}
    </Dialog>
  );
}

function MergeDialog({ view, onClose }: { view: OrderView; onClose: () => void }) {
  const plan = useFloorPlan();
  const navigate = useNavigate();
  const { fail, toast } = useUi();
  const [target, setTarget] = useState<{ id: string; name: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const merge = async () => {
    if (!target) return;
    setBusy(true);
    try {
      const v = await action<OrderView>('restaurant.merge', { from: view.order.id, into: target.id });
      toast(t('more.merged', { name: v.order.name }));
      navigate(`/pos/order/${v.order.id}`, { replace: true });
      onClose();
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  };
  const others = [
    ...(plan?.tables ?? []).filter((x) => x.order && x.order.id !== view.order.id).map((x) => ({ id: x.order!.id, name: t('table.name', { name: x.name }), total: x.order!.total })),
    ...(plan?.other_orders ?? []).filter((o) => o.id !== view.order.id).map((o) => ({ id: o.id, name: o.name, total: o.total })),
  ];
  if (target) {
    return (
      <Confirm
        title={t('more.merge')}
        body={t('more.merge_confirm', { from: view.order.name, into: target.name })}
        confirm={t('more.merge')}
        onConfirm={merge}
        onClose={() => setTarget(null)}
        busy={busy}
      />
    );
  }
  return (
    <Dialog title={t('more.merge')} onClose={onClose}>
      <p className="muted">{t('more.merge_pick', { name: view.order.name })}</p>
      {!plan ? (
        <Skeleton h={120} />
      ) : others.length === 0 ? (
        <EmptyState icon="merge" title={t('more.no_other_bills')} />
      ) : (
        <div className="pick-grid">
          {others.map((o) => (
            <button key={o.id} className="pick" onClick={() => setTarget(o)}>
              <strong>{o.name}</strong>
              <span className="muted">{money(o.total)}</span>
            </button>
          ))}
        </div>
      )}
    </Dialog>
  );
}

function SplitDialog({ view, onClose, onDone }: { view: OrderView; onClose: () => void; onDone: (v: OrderView) => void }) {
  const navigate = useNavigate();
  const { fail, toast } = useUi();
  const lines = view.lines.filter((l) => l.state === 'active');
  const [take, setTake] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(false);
  const total = lines.reduce((s, l) => s + Math.round((l.total * (take[l.id] ?? 0)) / l.qty), 0);
  const chosen = Object.entries(take).filter(([, n]) => n > 0);
  const split = async () => {
    setBusy(true);
    try {
      const r = await action<{ original: OrderView; split: OrderView }>('restaurant.split', {
        order: view.order.id,
        items: chosen.map(([line, q]) => ({ line, qty: q })),
      });
      onDone(r.original);
      toast(t('more.split_done'));
      navigate(`/pos/order/${r.split.order.id}`);
      onClose();
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={t('more.split')}
      onClose={onClose}
      footer={
        <>
          <span className="muted">{t('more.split_total', { total: money(total) })}</span>
          <Button variant="primary" icon="split" onClick={split} busy={busy} disabled={chosen.length === 0}>
            {t('more.split_submit')}
          </Button>
        </>
      }
    >
      <p className="muted">{t('more.split_hint')}</p>
      <div className="split-list">
        {lines.map((l) => {
          const n = take[l.id] ?? 0;
          return (
            <div key={l.id} className={`split-row${n > 0 ? ' active' : ''}`}>
              <span className="line-main">
                <span className="line-name">
                  {fmtQty(l.qty)}× {l.name}
                </span>
                <span className="muted">{money(l.total)}</span>
              </span>
              <div className="stepper">
                <IconButton icon="minus" label={t('common.less')} onClick={() => setTake((x) => ({ ...x, [l.id]: Math.max(0, n - 1) }))} />
                <span className="stepper-value">{n}</span>
                <IconButton icon="plus" label={t('common.more')} onClick={() => setTake((x) => ({ ...x, [l.id]: Math.min(l.qty, n + 1) }))} />
              </div>
            </div>
          );
        })}
      </div>
    </Dialog>
  );
}

export function DiscountDialog({ view, onClose, onDone }: { view: OrderView; onClose: () => void; onDone: (v: OrderView) => void }) {
  const { fail, toast } = useUi();
  const [percent, setPercent] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const apply = async (p: number) => {
    setBusy(true);
    try {
      onDone(await action<OrderView>('restaurant.discount', { order: view.order.id, percent: p }));
      toast(p ? t('more.discount_done', { n: p }) : t('more.discount_removed'));
      onClose();
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={t('more.discount')}
      onClose={onClose}
      size="sm"
      footer={
        <>
          {view.order.discount > 0 ? (
            <Button variant="ghost" onClick={() => void apply(0)} busy={busy}>
              {t('more.discount_remove')}
            </Button>
          ) : (
            <span />
          )}
          <Button variant="primary" icon="percent" disabled={percent === null} busy={busy} onClick={() => percent !== null && void apply(percent)}>
            {percent !== null ? t('more.discount_apply', { amount: money(Math.round((view.order.subtotal * percent) / 100)) }) : t('more.discount')}
          </Button>
        </>
      }
    >
      <div className="guest-grid">
        {[5, 10, 15, 20, 25, 50].map((p) => (
          <button key={p} className={`guest-btn${percent === p ? ' active' : ''}`} aria-pressed={percent === p} onClick={() => setPercent(p)}>
            {p}%
          </button>
        ))}
      </div>
    </Dialog>
  );
}

/** Cancels an open bill with a reason; `after` is where to go next (the tables by default). */
export function CancelDialog({ view, onClose, after = '/pos' }: { view: OrderView; onClose: () => void; after?: string }) {
  const navigate = useNavigate();
  const { fail, toast } = useUi();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const cancel = async () => {
    setBusy(true);
    try {
      await action('restaurant.cancel', { order: view.order.id, reason: reason.trim() });
      toast(t('more.cancelled', { name: view.order.name }));
      navigate(after);
    } catch (err) {
      fail(err instanceof ApiError ? err : err);
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={t('more.cancel')}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.back')}
          </Button>
          <Button variant="danger" icon="trash" onClick={cancel} busy={busy} disabled={reason.trim().length < 3}>
            {t('more.cancel_submit')}
          </Button>
        </>
      }
    >
      <p>{t('more.cancel_body')}</p>
      <Field label={t('line.reason')}>
        <input className="input" value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} data-autofocus />
      </Field>
    </Dialog>
  );
}
